// Centralized source of truth for all prototype call scripts.
// Each line maps 1:1 to a pre-recorded MP3 in /public/audio/{id}.mp3
// Generated via /app/scripts/generate_voices.py (ElevenLabs eleven_v3, with
// tashkeel + audio tags for emotional prosody and dialect-specific voices).

// Display labels for the debtor's voice profile (used by Debtor360 to show the
// AI voice line under the telephony header).  The voice ID here is the *debtor*
// voice — the Debtor360 agent uses a different voice (Ali Salman) so the two
// parties never share a voice.
export const VOICE_PROFILES = {
  'SA-M': { label: 'Saudi • Male',     name: 'Hasawi'  },
  'SA-F': { label: 'Saudi • Female',   name: 'Lama'    },
  'EG-M': { label: 'Egyptian • Male',  name: 'Hany'    },
  'EG-F': { label: 'Egyptian • Female',name: 'Farida'  },
  'JO-M': { label: 'Jordanian • Male', name: 'Faris'   },
  'JO-F': { label: 'Jordanian • Female',name: 'Rafoush'},
  'LB-M': { label: 'Lebanese • Male',  name: 'Fadi'    },
  'LB-F': { label: 'Lebanese • Female',name: 'Rafoush' },
  'AE-M': { label: 'Emirati • Male',   name: 'Hasawi'  },
  'AE-F': { label: 'Emirati • Female', name: 'Salma'   },
  'SY-M': { label: 'Syrian • Male',    name: 'Faris'   },
  'SY-F': { label: 'Syrian • Female',  name: 'Rafoush' },
};

export const profileKey = (nationality = 'SA', gender = 'M') =>
  `${nationality}-${gender}` in VOICE_PROFILES ? `${nationality}-${gender}` : 'SA-M';

// Debtor360 — flagship Saudi call.  Agent voice is fixed (Ali Salman, warm
// Saudi male).  Debtor responses are MSA-light Arabic, voiced once per debtor
// profile so the audio reflects the case's nationality/gender.
export const DEBTOR360_AGENT_NAME = 'Fares';
export const DEBTOR360_AGENT_VOICE = { label: 'Saudi • Male', name: 'Ali Salman' };

export const DEBTOR360_SCRIPT = [
  { id: 'd360-a-01', who: 'agent',
    text: 'السلام عليكم… أتحدّث مع أبو فهد؟ معك فارس، من شركة الوصل الوطنية. كيف حالك؟' },
  { id: 'd360-d-01', who: 'debtor', profilePer: true,
    text: 'وعليكم السلام… نعم، أنا. تفضّل.' },
  { id: 'd360-a-02', who: 'agent',
    text: 'حبّيت أحدّثك بخصوص حسابك المتأخر… عندنا حلّ يناسبك بإذن الله.' },
  { id: 'd360-d-02', who: 'debtor', profilePer: true,
    text: 'أعرف… والله هذا الشهر صعب عليّ، واجهتُ ظروفاً ما كنتُ أتوقّعها.' },
  { id: 'd360-a-03', who: 'agent',
    text: 'أتفهّمك تماماً يا أبو فهد… عندنا خطة سداد ميسّرة، نوزّع المبلغ على ستة أشهر، بدون أي فوائد إضافية. تناسبك؟' },
];

// Audio file id for a Debtor360 debtor line, parameterized by debtor profile.
export const debtor360DebtorAudioId = (lineId, profile) => `${lineId}-${profile}`;

// SupervisorCockpit — four dialect-distinct calls.  Each call uses two
// different voices (agent ≠ debtor) within the same dialect.
export const SUPERVISOR_CALLS = [
  // ────────── Saudi (Najdi) ──────────
  {
    case: 'CASE-10412',
    dialect: 'Saudi',
    agent: 'Layla Bin Ali',  agentVoice:  { label: 'Saudi • Female', name: 'Layan' },
    debtor: 'Fahad Al-Otaibi', debtorVoice: { label: 'Saudi • Male',   name: 'Hasawi' },
    dur: '04:21', state: 'Talking', qa: 'OK',
    lines: [
      { id: 'sup-10412-a1', who: 'agent',
        text: 'السلام عليكم أستاذ فهد… معك ليلى من شركة الوصل الوطنية. كيف الحال اليوم؟' },
      { id: 'sup-10412-d1', who: 'debtor',
        text: 'وعليكم السلام… الحمدلله، بس صراحة الواحد مكتّف هالأيام، وعندي شغل.' },
      { id: 'sup-10412-a2', who: 'agent',
        text: 'أتفهّم عليك تماماً يا أستاذ فهد… ودّيت أحكي معك بخصوص حسابك المتأخر، عندنا خطة سداد ميسّرة على ستة أشهر بدون أي رسوم. تناسبك؟' },
    ],
  },

  // ────────── Egyptian (Cairene) ──────────
  {
    case: 'CASE-10418',
    dialect: 'Egyptian',
    agent: 'Marwa Hassan',  agentVoice:  { label: 'Egyptian • Female', name: 'Fatima' },
    debtor: 'Sara Farouk',  debtorVoice: { label: 'Egyptian • Female', name: 'Farida' },
    dur: '01:08', state: 'Talking', qa: 'Watch',
    lines: [
      { id: 'sup-10418-a1', who: 'agent',
        text: 'ألو أستاذة سارة… أنا مروة، بكلّمك من شركة الوصل الوطنية. عاملة إيه النهارده؟' },
      { id: 'sup-10418-d1', who: 'debtor',
        text: 'أيوه يا فندم… الحمدلله. بس الصراحة الشهر ده ضغط عليّ جداً، وأنا فاهمة إن فيه قسط متأخر.' },
      { id: 'sup-10418-a2', who: 'agent',
        text: 'متقلقيش يا سارة… عندنا خطة سداد مرنة، نقدر نقسّم المبلغ على ستة شهور من غير أي فوايد زيادة. إيه رأيك؟' },
    ],
  },

  // ────────── Jordanian (Levantine) ──────────
  {
    case: 'CASE-10423',
    dialect: 'Jordanian',
    agent: 'Rami Khoury',  agentVoice:  { label: 'Jordanian • Male', name: 'Rami' },
    debtor: 'Ahmed Bani Hani', debtorVoice: { label: 'Jordanian • Male', name: 'Faris' },
    dur: '02:55', state: 'On hold', qa: 'OK',
    lines: [
      { id: 'sup-10423-a1', who: 'agent',
        text: 'مرحبا أستاذ أحمد… أنا رامي من شركة الوصل الوطنية. كيفك اليوم؟' },
      { id: 'sup-10423-d1', who: 'debtor',
        text: 'أهلين رامي… والله الواحد ماشي حاله، بس الشهر هاد صعب عليّ، وأنا عارف إنّه عندي قسط متأخر.' },
      { id: 'sup-10423-a2', who: 'agent',
        text: 'والله بفهم عليك تماماً يا أبو أحمد… عنّا خطة سداد بستة أقساط بدون أي زيادة. شو رأيك نلاقي حلّ سوا؟' },
    ],
  },

  // ────────── Urdu (Pakistani expatriate) ──────────
  {
    case: 'CASE-10401',
    dialect: 'Urdu',
    agent: 'Sara Khan',   agentVoice:  { label: 'Urdu • Female', name: 'Reva' },
    debtor: 'Imran Malik', debtorVoice: { label: 'Urdu • Male',  name: 'Amit' },
    dur: '00:42', state: 'Dialling', qa: 'OK',
    lines: [
      { id: 'sup-10401-a1', who: 'agent',
        text: 'السلام علیکم عمران صاحب… میں سارا ہوں الوصل کمپنی سے۔ امید ہے آپ خیریت سے ہوں گے۔' },
      { id: 'sup-10401-d1', who: 'debtor',
        text: 'وعلیکم السلام… جی محترمہ، فرمائیے۔ میں سمجھ گیا کیوں آپ کی کال آئی ہے۔' },
      { id: 'sup-10401-a2', who: 'agent',
        text: 'جی، آپ کے کھاتے کی قسط تاخیر سے ہے… لیکن پریشان نہ ہوں، ہمارے پاس ایک آسان ادائیگی کا منصوبہ ہے، چھ مہینوں میں، بغیر کسی اضافی فیس کے۔' },
    ],
  },
];
